import React from "react";
import { NavLink, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { KidzkulamLogo } from "@/components/Logo";
import {
  LayoutDashboard, Users, Baby, ClipboardCheck, IndianRupee, Camera,
  Car, ClipboardList, GraduationCap, BarChart3, LogOut, Menu, X, Settings as SettingsIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ROLE_NAV = {
  admin: [
    ["Dashboard", "/", LayoutDashboard],
    ["Enquiries", "/enquiries", ClipboardList],
    ["Children", "/children", Baby],
    ["Attendance", "/attendance", ClipboardCheck],
    ["Fees", "/fees", IndianRupee],
    ["Daily Updates", "/daily-updates", Camera],
    ["Pickup / Drop", "/pickup-drop", Car],
    ["Staff", "/staff", Users],
    ["User Management", "/user-management", Users],
    ["Reports", "/reports", BarChart3],
    ["Settings", "/settings", SettingsIcon],
  ],
  front_desk: [
    ["Enquiries", "/enquiries", ClipboardList],
    ["Children", "/children", Baby],
    ["Fees", "/fees", IndianRupee],
    ["Pickup / Drop", "/pickup-drop", Car],
    ["Attendance", "/attendance", ClipboardCheck],
  ],
  teacher: [
    ["My Class", "/children", Baby],
    ["Attendance", "/attendance", ClipboardCheck],
    ["Daily Updates", "/daily-updates", Camera],
    ["Pickup / Drop", "/pickup-drop", Car],
  ],
  parent: [
    ["My Child", "/parent", GraduationCap],
  ],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const nav = ROLE_NAV[user?.role] || [];

  const roleLabel = { admin: "Owner", teacher: "Teacher", front_desk: "Front Desk", parent: "Parent" }[user?.role] || "";

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-white/70 backdrop-blur-xl">
        <div className="px-6 py-6">
          <KidzkulamLogo />
        </div>
        <nav className="px-3 py-2 space-y-1 flex-1">
          {nav.map(([label, to, Icon]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/\s|\//g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold transition-all",
                  isActive
                    ? "bg-primary/12 text-[hsl(142_60%_22%)]"
                    : "text-foreground/70 hover:bg-muted hover:text-foreground"
                )
              }
            >
              <Icon size={18} strokeWidth={2.5} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary/40 flex items-center justify-center font-bold text-secondary-foreground">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{roleLabel}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className="p-2 rounded-xl hover:bg-muted"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white/85 backdrop-blur-xl border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <KidzkulamLogo size={32} />
          <button data-testid="mobile-menu-btn" className="p-2 rounded-xl hover:bg-muted" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="border-t border-border p-3 space-y-1 max-h-[70vh] overflow-auto">
            {nav.map(([label, to, Icon]) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${label.toLowerCase().replace(/\s|\//g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl font-semibold",
                    isActive ? "bg-primary/12 text-[hsl(142_60%_22%)]" : "hover:bg-muted"
                  )
                }
              >
                <Icon size={18} strokeWidth={2.5} />
                {label}
              </NavLink>
            ))}
            <button
              data-testid="mobile-logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-destructive hover:bg-destructive/10"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        )}
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
